import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as XLSX from 'xlsx';
import { UserService } from '../services/user/user.service';
import { lastValueFrom, Subject, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { ListStateService } from '../services/list-state/list-state.service';
import moment from 'moment';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { ToasterService, AuthService } from '@ui-nuvertos/auth';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { openInNewTab } from '../utils/url.utils';

@Component({
  selector: 'lib-user-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './user-list.html',
  styleUrl: './user-list.css',
})
export class UserList implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  exportFilename = `users_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];
  totalCount: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;
  pageIndex: number = 1;
  rowsPerPage: number = 10;

  // Filter values
  name: string = '';
  email: string = '';
  phone: string = '';
  showInactives = false;
  role: string = '';
  currentUserRole: string = '';
  private destroy$ = new Subject<void>();

  roleOptions = [
    { label: 'All', value: '' },
    { label: 'Admin', value: 'admin' },
    { label: 'User', value: 'user' },
    { label: 'Manager', value: 'manager' },
  ];

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'phone', header: 'Phone' },
    { key: 'role', header: 'Role' },
    { key: 'created_at', header: 'Created Date' },
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'toggle',
      label: 'Status',
      tooltip: 'Toggle Active/Inactive',
      type: 'toggle'
    },
    {
      key: 'view',
      label: 'View',
      tooltip: 'View',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    }
  ];

  constructor(
    private router: Router,
    private userService: UserService,
    private toasterService: ToasterService,
    private authService: AuthService,
    private dialogService: DialogService,
    private exportApiService: ExportApiService,
  ) {}

  ngOnInit() {
    // Subscribe to currentUser$ to get role once auth loads from IndexedDB
    this.authService.currentUser$.subscribe(user => {
      if (user && user.role) {
        const previousRole = this.currentUserRole;
        this.currentUserRole = user.role;
        // If role just loaded and we already have data, re-filter
        if (!previousRole && this.tableData.length > 0) {
          this.filterTableByRole();
        }
      }
    });

    this.initializeFilterConfigs();
    // Subscribe to URL page changes
    this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
      this.loadUsers(page);
    });
  }

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterTableByRole(): void {
    if (!this.currentUserRole) return;
    const currentRolePriority = this.getRolePriority(this.currentUserRole);
    this.tableData = this.tableData.filter(row => {
      const userRolePriority = this.getRolePriority(row.role || '');
      return userRolePriority >= currentRolePriority;
    });
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'name',
        type: 'text',
        placeholder: 'Name',
        value: this.name,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'email',
        type: 'text',
        placeholder: 'Email',
        value: this.email,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'phone',
        type: 'text',
        placeholder: 'Phone',
        value: this.phone,
        showInAccordion: true,
        showInDialog: false
      }
      // {
      //   key: 'role',
      //   type: 'select',
      //   placeholder: 'Role',
      //   options: this.roleOptions,
      //   optionLabel: 'label',
      //   optionValue: 'value',
      //   value: this.role,
      //   showInAccordion: true,
      //   showInDialog: false
      // }
    ];
  }

  async loadUsers(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const queryParams: any = {
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        limit: this.rowsPerPage,
      };
      
      if (this.showInactives) {
        queryParams.include_inactive = 'true';
      }

      // Attach filters
      if (this.name && this.name.trim()) {
        queryParams.name = this.name.trim();
      }
      if (this.email && this.email.trim()) {
        queryParams.email = this.email.trim();
      }
      if (this.phone && this.phone.trim()) {
        queryParams.phone = this.phone.trim();
      }
      // if (this.role && this.role.trim()) {
      //   queryParams.role = this.role.trim();
      // }

      const userResponse = await lastValueFrom(this.userService.getAllUsers(queryParams));
      
      if (userResponse?.users && Array.isArray(userResponse.users)) {
        const rows = userResponse.users || [];
        this.tableData = rows.map((row: any) => {
          // Extract role from centers array (first center's role)
          const userRole = row.centers && row.centers.length > 0 
            ? row.centers[0].role 
            : '-';
          
          return {
            id: row.id,
            name: row.username || '-',
            email: row.email || '-',
            phone: row.phone_number || '-',
            role: userRole,
            is_active: row.is_active,
            created_at: moment(row.created_at).format('DD/MM/YYYY') || '-',
            raw: row
          };
        });
        this.totalCount = userResponse.total ?? 0;
        const totalPagesFromResponse = userResponse.total_pages ?? Math.ceil((this.totalCount || 0) / this.rowsPerPage);
        this.totalPages = totalPagesFromResponse > 0 ? totalPagesFromResponse : (this.tableData.length > 0 ? 1 : 0);
        
        // SECURITY: Filter out users with higher privilege than current user
        // Only filter if role is loaded, otherwise show all (will be filtered when role loads)
        if (this.currentUserRole) {
          const currentRolePriority = this.getRolePriority(this.currentUserRole);
          this.tableData = this.tableData.filter(row => {
            const userRolePriority = this.getRolePriority(row.role || '');
            return userRolePriority >= currentRolePriority;
          });
        }
      } else {
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 0;
      }

    } catch (error) {
      console.log('Error loading users:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.toasterService.error('Error', 'Failed to load users');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    // Update local filter values from event.data
    this.name = event.data?.name || '';
    this.email = event.data?.email || '';
    this.phone = event.data?.phone || '';
    // this.role = event.data?.role || '';
    
    // Update filter configs
    this.initializeFilterConfigs();

    // Reset to page 1 when filters change
    this.listStateService.clearPage();
    // Always reload when filters change (URL subscription may not trigger if already on page 1)
    this.loadUsers(1);
  }

  onActionClicked(event: ActionEvent) {
    const row = event.row;
    if (!row) return;

    switch (event.action) {
      case 'toggle':
        this.toggleStatus(row);
        break;
      case 'view':
        this.router.navigate(['/settings/account_settings/users/view', row.id]);
        break;
      case 'edit':
        this.router.navigate(['/settings/account_settings/users/edit', row.id]);
        break;
    }
  }

  async toggleStatus(row: any) {
    // The toggle has already updated row.is_active via two-way binding
    // So we use the current value directly, ensuring it's a boolean
    const newStatus = Boolean(row.is_active);
    const statusText = newStatus ? 'activate' : 'deactivate';
    
    if (confirm(`Are you sure you want to ${statusText} "${row.name || row.id}"?`)) {
      try {
        this.isLoading = true;
        
        // Send only id and is_active (convert id to number)
        const updateData = {
          id: Number(row.id),
          is_active: newStatus
        };
        
        await lastValueFrom(
          this.userService.updateUser(updateData)
        );
        
        this.toasterService.success('Success', `User ${statusText}d successfully`);
        // Reload the list
        await this.loadUsers(this.pageIndex);
      } catch (error) {
        console.error('Error toggling status:', error);
        this.toasterService.error('Error', 'Failed to toggle user status');
        // Revert the toggle on error
        row.is_active = !newStatus;
      } finally {
        this.isLoading = false;
      }
    } else {
      // Revert the toggle if user cancels
      row.is_active = !newStatus;
    }
  }

  onPageChanged(page: number) {
    // Update URL - this triggers the subscription which loads the data
    this.listStateService.setPage(page);
  }

  onShowInactivesChange() {
    // Reset to page 1 when inactives toggle changes
    this.listStateService.clearPage();
    this.loadUsers(1);
  }

  onColumnsSelected(selectedColumns: string[]) {
    // TODO: Integrate with backend column preferences
    // For now, simply reload data to reflect any necessary transformations
    this.loadUsers(this.pageIndex);
  }

  exportCsv() {
    const data = this.tableData.map(row => ({
      Name: row.name,
      Email: row.email,
      Phone: row.phone,
      Role: row.role,
      Status: row.is_active ? 'Active' : 'Inactive',
      'Created Date': row.created_at
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, `${this.exportFilename}.csv`);
  }

  exportXlsx() {
    this.openExportDialog('excel');
  }

  exportPdf() {
    // PDF export implementation can be added here
    this.toasterService.info('Info', 'PDF export coming soon');
  }

  toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  // Get role priority (lower number = higher privilege)
  getRolePriority(roleName: string): number {
    switch (roleName) {
      case 'Super Admin': return 1;
      case 'Admin': return 2;
      case 'Manager': return 3;
      case 'Accountant': return 4;
      case 'Viewer': return 5;
      default: return 999; // Unknown or unassigned roles have lowest priority
    }
  }
  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Users',
      width: '500px',
      data: {
        module: 'user',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        hasActiveFilters: this.hasActiveFilters(),
        defaultFormat: format
      }
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  private getCurrentFilters(): Record<string, any> {
    return {
      name: this.name?.trim() || undefined,
      email: this.email?.trim() || undefined,
      phone: this.phone?.trim() || undefined,
      include_inactive: this.showInactives ? 'true' : undefined,
    };
  }

  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started',
          'Your export is processing. Click here to view progress →',
          5000
        );
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      }
    });
  }
  private hasActiveFilters(): boolean {
    const filters = this.getCurrentFilters();
    return Object.values(filters).some(
      value =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
  }
}

